import { useEffect, useRef, useState } from 'react';
import { Bot, Plug, Save, Send, Settings, ShoppingBag, Sparkles, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ProductCheckoutModal from '../../../billing/ProductCheckoutModal';
import { useAuthSession } from '../../../../contexts/AuthSessionContext';
import { IntegrationCard } from '../components/configuration/AIConfigurationUI.tsx';
import {
  fetchManagerIntegrationSettings,
  fetchOrderControlSettings,
  testPromptConfigChat,
  updateManagerIntegrationSettings,
  updateOrderControlSettings,
} from '../services/api.ts';
import type { ManagerIntegrationSettings } from '../services/api.ts';
import { notifyAtendabotOrderControl } from '../utils/orderControl.ts';
import { PRODUCT_IMAGES } from '../../../../branding/assets';
import '../components/configuration/AIConfigurationUI.css';
import './FormPrompt.css';

type PreviewMessage = {
  role: 'user' | 'assistant';
  content: string;
};

const PRODUCTS = [
  {
    key: 'reservo',
    name: 'Reservo',
    logo: PRODUCT_IMAGES.reservoIcon,
    logoClassName: 'is-reservo',
    theme: 'reservo',
    route: '/reservo',
    description: 'Permite que a IA consulte horários e registre reservas durante a conversa.',
    preferenceKey: 'usar_reservo_reservas',
    question: 'A IA deve fazer reservas pelo WhatsApp?',
    helper: 'Quando ativo, a IA consulta horários e registra a reserva diretamente no Reservo.',
  },
  {
    key: 'feedback_hero',
    name: 'FeedbackHero',
    logo: PRODUCT_IMAGES.feedbackHero,
    logoClassName: 'is-feedback-hero',
    theme: 'feedback-hero',
    route: '/feedback-hero',
    description: 'Permite que a IA solicite avaliações após os atendimentos e pedidos.',
    preferenceKey: 'usar_feedbackhero_avaliacao_pedidos',
    question: 'Pedir avaliação dos pedidos finalizados?',
    helper: 'Quando ativo, o cliente escolhe de 1 a 5 estrelas e pode enviar um comentário.',
  },
  {
    key: 'cv',
    name: 'Currículo Certo',
    logo: PRODUCT_IMAGES.cv,
    logoClassName: 'is-cv',
    theme: 'cv',
    route: '/cv',
    description: 'Permite que a IA apresente vagas abertas e envie o link de candidatura.',
    preferenceKey: 'usar_cv_vagas',
    question: 'A IA deve responder sobre vagas?',
    helper: 'Quando ativo, a IA apresenta as vagas abertas e envia o link de candidatura.',
  },
] as const;

const DEFAULT_INTEGRATION_SETTINGS: ManagerIntegrationSettings = {
  usar_reservo_reservas: false,
  usar_feedbackhero_avaliacao_pedidos: false,
  usar_cv_vagas: false,
};

const IntegrationPreference = ({
  question,
  helper,
  value,
  disabled,
  onChange,
}: {
  question: string;
  helper: string;
  value: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <div className="min-w-0">
      <p className="text-sm font-semibold text-slate-800">{question}</p>
      <p className="mt-1 text-xs leading-5 text-slate-600">{helper}</p>
    </div>
    <div className="flex shrink-0 items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={question}
        disabled={disabled}
        onClick={() => onChange(!value)}
        className={`relative inline-flex h-6 w-11 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${value ? 'bg-emerald-600' : 'bg-slate-300'}`}
      >
        <span className={`pointer-events-none mt-0.5 inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-[1.375rem]' : 'translate-x-0.5'}`} />
      </button>
      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${value ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
        {value ? 'Ativo' : 'Inativo'}
      </span>
    </div>
  </div>
);

const ManagerPromptSettings = () => {
  const navigate = useNavigate();
  const { hasProductAccess, isFoodUser } = useAuthSession();
  const [checkoutProductKey, setCheckoutProductKey] = useState<string | null>(null);
  const [messages, setMessages] = useState<PreviewMessage[]>([]);
  const [message, setMessage] = useState('');
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState('');
  const [integrationSettings, setIntegrationSettings] = useState(DEFAULT_INTEGRATION_SETTINGS);
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);
  const [integrationsLoaded, setIntegrationsLoaded] = useState(false);
  const [savingIntegrations, setSavingIntegrations] = useState(false);
  const [integrationsDirty, setIntegrationsDirty] = useState(false);
  const [integrationsError, setIntegrationsError] = useState('');
  const [orderControlEnabled, setOrderControlEnabled] = useState(false);
  const [orderControlLoading, setOrderControlLoading] = useState(true);
  const [orderControlSaving, setOrderControlSaving] = useState(false);
  const [orderControlError, setOrderControlError] = useState('');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    let active = true;

    const loadIntegrationSettings = async () => {
      try {
        const settings = await fetchManagerIntegrationSettings();
        if (!active) return;
        setIntegrationSettings({
          usar_reservo_reservas: settings?.usar_reservo_reservas === true,
          usar_feedbackhero_avaliacao_pedidos:
            settings?.usar_feedbackhero_avaliacao_pedidos === true,
          usar_cv_vagas: settings?.usar_cv_vagas === true,
        });
        setIntegrationsLoaded(true);
        setIntegrationsError('');
      } catch (error: any) {
        if (!active) return;
        setIntegrationsError(
          error?.response?.data?.error
            || 'Não foi possível carregar as preferências de integração. Atualize a página para tentar novamente.',
        );
      } finally {
        if (active) setLoadingIntegrations(false);
      }
    };

    loadIntegrationSettings();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isFoodUser) {
      setOrderControlLoading(false);
      return undefined;
    }

    let active = true;
    const loadOrderControl = async () => {
      try {
        const settings = await fetchOrderControlSettings();
        if (!active) return;
        setOrderControlEnabled(settings?.pedidos_habilitados === true);
        setOrderControlError('');
      } catch (error: any) {
        if (!active) return;
        setOrderControlError(
          error?.response?.data?.error
            || 'Não foi possível carregar a configuração de pedidos. Atualize a página para tentar novamente.',
        );
      } finally {
        if (active) setOrderControlLoading(false);
      }
    };

    loadOrderControl();
    return () => {
      active = false;
    };
  }, [isFoodUser]);

  const toggleOrderControl = async () => {
    if (orderControlLoading || orderControlSaving) return;
    const nextValue = !orderControlEnabled;
    setOrderControlSaving(true);
    setOrderControlError('');
    try {
      const settings = await updateOrderControlSettings({ pedidos_habilitados: nextValue });
      const enabled = settings?.pedidos_habilitados === true;
      setOrderControlEnabled(enabled);
      notifyAtendabotOrderControl(enabled);
      toast.success('Configuração de pedidos salva com sucesso.');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || 'Não foi possível atualizar a configuração de pedidos.';
      setOrderControlError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setOrderControlSaving(false);
    }
  };

  const changeIntegrationPreference = (
    key: keyof ManagerIntegrationSettings,
    value: boolean,
  ) => {
    setIntegrationSettings((current) => ({ ...current, [key]: value }));
    setIntegrationsDirty(true);
    setIntegrationsError('');
  };

  const saveIntegrationSettings = async () => {
    if (!integrationsLoaded || loadingIntegrations || savingIntegrations || !integrationsDirty) return;
    setSavingIntegrations(true);
    setIntegrationsError('');
    try {
      const saved = await updateManagerIntegrationSettings(integrationSettings);
      setIntegrationSettings(saved);
      setIntegrationsDirty(false);
      toast.success('Preferências da IA salvas com sucesso.');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || 'Não foi possível salvar as preferências de integração.';
      setIntegrationsError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSavingIntegrations(false);
    }
  };

  const sendTestMessage = async () => {
    const normalizedMessage = message.trim();
    if (!normalizedMessage || testing) return;
    const history = [...messages];
    setMessages((current) => [...current, { role: 'user', content: normalizedMessage }]);
    setMessage('');
    setTestError('');
    setTesting(true);
    try {
      const response = await testPromptConfigChat({ message: normalizedMessage, history });
      const assistantText = String(
        response?.response ?? response?.resposta ?? response?.message ?? 'Sem resposta no momento.',
      ).trim();
      setMessages((current) => [...current, { role: 'assistant', content: assistantText }]);
    } catch (error: any) {
      setTestError(error?.response?.data?.error || 'Não foi possível testar a conversa agora.');
    } finally {
      setTesting(false);
      window.requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const handleProductAction = (product: typeof PRODUCTS[number]) => {
    if (hasProductAccess(product.key)) {
      navigate(product.route);
      return;
    }
    setCheckoutProductKey(product.key);
  };

  return (
    <div className="min-h-full bg-[#f6f7fb] px-3 py-6 text-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-[1400px] space-y-5">
        <header className="mb-1 text-center">
          <h1 className="flex items-center justify-center gap-3 text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
            <Settings size={28} className="text-[#2f7d72]" />
            Configurações
          </h1>
          <p className="mx-auto mt-3 max-w-3xl text-sm leading-6 text-slate-600">
            O comportamento da IA é mantido pelos administradores do sistema. Aqui você pode conferir o resultado antes de colocá-lo em produção e ativar integrações complementares.
          </p>
        </header>

      {isFoodUser && (
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
        <div className="mb-5 flex items-start gap-3">
          <span className="rounded-xl bg-emerald-100 p-2.5 text-emerald-700"><ShoppingBag size={21} /></span>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Pedidos</h2>
            <p className="mt-1 text-sm text-slate-600">Defina se a IA deve controlar pedidos nesta unidade.</p>
          </div>
        </div>
        <IntegrationPreference
          question="A IA deve controlar pedidos por este atendimento?"
          helper="Quando ativo, a IA pode montar, registrar, acompanhar e cancelar pedidos pelo WhatsApp. Quando inativo, ela ainda pode enviar o link indicado no atendimento, mas não controla pedidos e os menus de Pedidos e Histórico ficam indisponíveis."
          value={orderControlEnabled}
          disabled={orderControlLoading || orderControlSaving}
          onChange={() => toggleOrderControl()}
        />
        {!orderControlEnabled && !orderControlLoading && (
          <p className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            Solicitações de acompanhamento, status, confirmação, alteração ou cancelamento receberão a mensagem definida pelos administradores do sistema, sem executar nenhuma ação de pedido.
          </p>
        )}
        {orderControlError && (
          <p className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700" role="alert">
            {orderControlError}
          </p>
        )}
      </section>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
        <div className="mb-5 flex items-start gap-3">
          <span className="rounded-xl bg-violet-100 p-2.5 text-violet-700"><Plug size={21} /></span>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Integrações complementares</h2>
            <p className="mt-1 text-sm text-slate-600">Ative os produtos que ampliam o que a IA consegue fazer durante o atendimento.</p>
          </div>
        </div>
        <div className="space-y-3">
          {PRODUCTS.map((product) => {
            const active = hasProductAccess(product.key);
            return (
              <IntegrationCard
                key={product.key}
                logo={product.logo}
                logoClassName={product.logoClassName}
                theme={product.theme}
                showStatus={false}
                name={product.name}
                status={active ? 'connected' : 'disconnected'}
                description={product.description}
                actionLabel={active ? `Abrir ${product.name}` : `Ativar ${product.name}`}
                onAction={() => handleProductAction(product)}
              >
                {active && (
                  <IntegrationPreference
                    question={product.question}
                    helper={product.helper}
                    value={integrationSettings[product.preferenceKey]}
                    disabled={!integrationsLoaded || loadingIntegrations || savingIntegrations}
                    onChange={(value) => changeIntegrationPreference(product.preferenceKey, value)}
                  />
                )}
              </IntegrationCard>
            );
          })}
        </div>
        {integrationsError && (
          <p className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700" role="alert">
            {integrationsError}
          </p>
        )}
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={saveIntegrationSettings}
            disabled={!integrationsLoaded || loadingIntegrations || savingIntegrations || !integrationsDirty}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Save size={17} />
            {savingIntegrations ? 'Salvando…' : loadingIntegrations ? 'Carregando…' : 'Salvar preferências'}
          </button>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-slate-50 p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <span className="rounded-xl bg-emerald-100 p-2.5 text-emerald-700"><Sparkles size={21} /></span>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Testar comportamento da IA</h2>
              <p className="mt-1 text-sm text-slate-600">Esta simulação usa exatamente a configuração ativa e não envia mensagens ao WhatsApp.</p>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <div className="mx-auto max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-[#efeae2] shadow-inner" aria-label="Conversa de teste com a IA">
            <div className="flex items-center gap-3 bg-[#075e54] px-4 py-3 text-white">
              <span className="rounded-full bg-white/15 p-2"><Bot size={18} /></span>
              <div><strong>IA de atendimento</strong><p className="text-xs text-white/75">{testing ? 'digitando…' : 'online'}</p></div>
            </div>
            <div className="min-h-72 space-y-3 p-4" aria-live="polite">
              {!messages.length && (
                <p className="rounded-xl bg-white/75 p-4 text-center text-sm text-slate-500">Envie uma mensagem para visualizar como a IA responderá ao cliente.</p>
              )}
              {messages.map((item, index) => (
                <div key={`${item.role}-${index}`} className={`flex ${item.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] whitespace-pre-wrap rounded-xl px-3 py-2 text-sm leading-6 shadow-sm ${item.role === 'user' ? 'bg-[#d9fdd3]' : 'bg-white'}`}>
                    {item.content}
                  </div>
                </div>
              ))}
              {testing && <div className="w-fit rounded-xl bg-white px-4 py-2 text-sm text-slate-500">IA digitando…</div>}
            </div>
            <div className="flex items-end gap-2 border-t border-black/5 bg-[#f0f2f5] p-3">
              <textarea
                ref={inputRef}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    sendTestMessage();
                  }
                }}
                rows={1}
                placeholder="Digite uma mensagem de teste"
                aria-label="Mensagem para testar a IA"
                className="min-h-11 flex-1 resize-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-emerald-600"
              />
              <button type="button" onClick={() => { setMessages([]); setTestError(''); }} disabled={testing || !messages.length} className="flex h-11 w-11 items-center justify-center rounded-xl text-slate-600 hover:bg-slate-200 disabled:opacity-40" aria-label="Limpar conversa"><Trash2 size={18} /></button>
              <button type="button" onClick={sendTestMessage} disabled={testing || !message.trim()} className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50" aria-label="Enviar mensagem de teste"><Send size={18} /></button>
            </div>
            {testError && <p className="border-t border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700" role="alert">{testError}</p>}
          </div>
        </div>
      </section>


      <ProductCheckoutModal
        open={Boolean(checkoutProductKey)}
        // @ts-ignore ProductCheckoutModal is an untyped JSX component.
        initialProductKey={checkoutProductKey}
        allowedProductKeys={['reservo', 'feedback_hero', 'cv']}
        title="Ativar integrações"
        onClose={() => setCheckoutProductKey(null)}
      />
      </div>
    </div>
  );
};

export default ManagerPromptSettings;
